import React, { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, ListChecks, Film, Search, Clock, Globe, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";

/* ==========================================================
   PLANNED POSTS — VIEW ONLY
   Reads store.entries (the same data the Content Calendar
   writes) and lists every scheduled post in a table, filtered
   by Day / Week / Month / Year. No scheduling logic lives here.
========================================================== */

const PLATFORM_NAMES = {
    facebook: "Facebook",
    instagram: "Instagram",
    linkedin: "LinkedIn",
    instagram_story: "Insta Story",
    youtube_shorts: "YT Shorts",
};

const PLATFORM_ICONS = {
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin,
    instagram_story: Instagram,
    youtube_shorts: Youtube,
};

// Brand colours used for the glass platform chips (c = brand, t = lighter text tint for dark bg)
const PLATFORM_COLORS = {
    facebook:        { c: "24,119,242",  t: "#7ab4ff" },
    instagram:       { c: "225,48,108",  t: "#ff7aa8" },
    linkedin:        { c: "10,132,214",  t: "#5cc0ff" },
    instagram_story: { c: "193,53,213",  t: "#e08cf0" },
    youtube_shorts:  { c: "255,48,48",   t: "#ff8080" },
};
const DEFAULT_PLATFORM_COLOR = { c: "156,163,175", t: "#d1d5db" };

const PlatformChip = ({ id }) => {
    const Icon = PLATFORM_ICONS[id] || Globe;
    const { c, t } = PLATFORM_COLORS[id] || DEFAULT_PLATFORM_COLOR;
    return (
        <span className="inline-flex items-center gap-2.5 text-sm font-semibold whitespace-nowrap" style={{ color: t }}>
            {/* glass logo badge */}
            <span
                className="w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-md shrink-0"
                style={{
                    background: `linear-gradient(145deg, rgba(${c},0.30), rgba(${c},0.08))`,
                    border: `1px solid rgba(${c},0.45)`,
                    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.25), 0 0 14px rgba(${c},0.20)`,
                }}
            >
                <Icon size={13} style={{ color: t }} />
            </span>
            {PLATFORM_NAMES[id] || id}
        </span>
    );
};

const STATUS_STYLE = {
    scheduled: "bg-orange-500/10 text-orange-400 border-orange-500/40",
    sent: "bg-green-500/10 text-green-400 border-green-500/40",
    cancelled: "bg-white/5 text-gray-500 border-white/10",
};
const STATUS_LABEL = { scheduled: "Scheduled", sent: "Sent", cancelled: "Cancelled" };

// Same rule the calendar uses to decide a post's status.
const occStatus = (occ, now) => {
    if (occ.status === "cancelled") return "cancelled";
    if (occ.status === "sent" || new Date(occ.at) <= now) return "sent";
    return "scheduled";
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(startOfDay(d), -d.getDay());
const fmtShort = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });

const getRange = (view, cursor) => {
    if (view === "day") {
        const s = startOfDay(cursor);
        return [s, addDays(s, 1)];
    }
    if (view === "week") {
        const s = startOfWeek(cursor);
        return [s, addDays(s, 7)];
    }
    if (view === "month") {
        return [new Date(cursor.getFullYear(), cursor.getMonth(), 1), new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)];
    }
    return [new Date(cursor.getFullYear(), 0, 1), new Date(cursor.getFullYear() + 1, 0, 1)];
};

const rangeLabel = (view, cursor) => {
    if (view === "day") return cursor.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
    if (view === "week") {
        const s = startOfWeek(cursor);
        const e = addDays(s, 6);
        return `${fmtShort(s)} – ${fmtShort(e)}, ${e.getFullYear()}`;
    }
    if (view === "month") return cursor.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    return String(cursor.getFullYear());
};

const shiftCursor = (view, cursor, dir) => {
    if (view === "day") return addDays(cursor, dir);
    if (view === "week") return addDays(cursor, 7 * dir);
    if (view === "month") return new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1);
    return new Date(cursor.getFullYear() + dir, 0, 1);
};

const PAGE_SIZES = [10, 25, 50, 100];

// Page numbers with ellipsis, e.g. 1 2 3 … 9
const pageList = (cur, total) => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    const set = new Set([1, total, cur, cur - 1, cur + 1]);
    const nums = [...set].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
    const out = [];
    nums.forEach((n, i) => { if (i && n - nums[i - 1] > 1) out.push("…"); out.push(n); });
    return out;
};

const thumbUrl = (outputs) => outputs?.facebook?.url || outputs?.instagram?.url || outputs?.linkedin?.url || null;

const PlannedPosts = ({ store }) => {
    const [view, setView] = useState("month");
    const [cursor, setCursor] = useState(() => new Date());
    const [now, setNow] = useState(() => new Date());
    const [search, setSearch] = useState("");
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(t);
    }, []);

    // Flatten: one row per scheduled occurrence.
    const allPosts = useMemo(() => {
        const rows = [];
        (store.entries || []).forEach((e) => {
            if (e.kind === "note") return;
            (e.occurrences || []).forEach((o) => {
                rows.push({ key: o.id, at: new Date(o.at), entry: e, occ: o });
            });
        });
        return rows.sort((a, b) => a.at - b.at);
    }, [store.entries]);

    const [from, to] = getRange(view, cursor);
    const inRange = allPosts.filter((p) => p.at >= from && p.at < to);

    // Search: caption, hashtags, platform, status, date and time
    const q = search.trim().toLowerCase();
    const posts = !q ? inRange : inRange.filter(({ at, entry, occ }) => {
        const hay = [
            entry.caption,
            entry.hashtags,
            (entry.platforms || []).map((id) => PLATFORM_NAMES[id] || id).join(" "),
            STATUS_LABEL[occStatus(occ, now)],
            at.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
            at.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true }),
        ].join(" ").toLowerCase();
        return hay.includes(q);
    });

    // Pagination
    useEffect(() => { setPage(1); }, [search, pageSize, view, cursor]);
    const totalPages = Math.max(1, Math.ceil(posts.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const paged = posts.slice((safePage - 1) * pageSize, safePage * pageSize);

    return (
        <div className="p-4 sm:p-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
                    <ListChecks size={20} className="text-orange-500" />
                </div>
                <div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">Planned Posts</h2>
                    <p className="text-gray-400 text-xs sm:text-sm">All scheduled posts with platform, date and time.</p>
                </div>
            </div>

            {/* Filter toolbar */}
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <button onClick={() => setCursor((c) => shiftCursor(view, c, -1))} aria-label="Previous" className="p-2 rounded-lg border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-500/50 transition"><ChevronLeft size={16} /></button>
                    <button onClick={() => setCursor((c) => shiftCursor(view, c, 1))} aria-label="Next" className="p-2 rounded-lg border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-500/50 transition"><ChevronRight size={16} /></button>
                    <button onClick={() => setCursor(new Date())} className="px-3 py-2 rounded-lg border border-white/10 text-xs font-medium text-gray-300 hover:text-orange-400 hover:border-orange-500/50 transition">Today</button>
                    <span className="ml-1 text-sm sm:text-base font-semibold text-white">{rangeLabel(view, cursor)}</span>
                </div>
                <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                    {["day", "week", "month", "year"].map((v) => (
                        <button key={v} onClick={() => setView(v)} className={`px-3 py-2 text-xs font-medium capitalize transition ${view === v ? "bg-orange-600 text-white" : "text-gray-300 hover:bg-white/5"}`}>{v}</button>
                    ))}
                </div>
            </div>

            {/* Search + rows per page */}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span>Show</span>
                    <div className="relative">
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            className="appearance-none bg-black/40 border border-white/10 hover:border-orange-500/30 focus:border-orange-500/50 focus:outline-none rounded-lg pl-3 pr-7 py-1.5 text-sm text-gray-200 transition cursor-pointer"
                        >
                            {PAGE_SIZES.map((n) => <option key={n} value={n} className="bg-[#111]">{n}</option>)}
                        </select>
                        <ChevronDown size={13} className="text-gray-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                    <span>entries</span>
                </div>
                <div className="relative">
                    <Search size={14} className="text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search posts…"
                        className="bg-black/40 border border-white/10 focus:border-orange-500/40 rounded-lg pl-8 pr-3 py-2 text-sm text-gray-200 outline-none w-52 sm:w-64 transition"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="mt-3 rounded-2xl border border-white/10 bg-black/60 overflow-hidden">
                <div className="overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-orange-500/20 [&::-webkit-scrollbar-thumb]:rounded-full">
                    <table className="w-full min-w-[900px] text-sm text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/10">
                                {["Content", "Platform", "Date", "Time", "Status", "Caption"].map((h) => (
                                    <th key={h} className="px-6 py-5 text-[13px] font-semibold uppercase tracking-wider text-orange-500 whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {posts.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                                        {q ? "No posts match your search." : `No posts scheduled for this ${view}.`}
                                    </td>
                                </tr>
                            )}
                            {paged.map(({ key, at, entry, occ }) => {
                                const st = occStatus(occ, now);
                                const url = thumbUrl(entry.outputs);
                                const title = (entry.caption || "").trim() || "Untitled post";
                                const series = (entry.occurrences || []).length;
                                return (
                                    <tr key={key} className="border-b border-white/[0.06] last:border-b-0 hover:bg-white/[0.03] transition">
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-3 min-w-[220px] max-w-[300px]">
                                                <div className="w-11 h-11 rounded-full overflow-hidden bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0 text-orange-400 text-xs font-bold">
                                                    {url
                                                        ? <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                                        : <Film size={16} />}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-white truncate" title={title}>{title}</p>
                                                    <p className="text-xs text-gray-500 truncate">
                                                        {series > 1 ? `${entry.repeat} · ${series} posts` : "One-time post"}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col items-start gap-2">
                                                {(entry.platforms || []).map((id) => <PlatformChip key={id} id={id} />)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-white whitespace-nowrap">
                                            {at.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                                        </td>
                                        <td className="px-6 py-5 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1.5 font-medium text-orange-300/70">
                                                <Clock size={14} />
                                                {at.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5">
                                            <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-semibold border ${STATUS_STYLE[st]}`}>{STATUS_LABEL[st]}</span>
                                        </td>
                                        <td className="px-6 py-5 text-gray-400 max-w-[260px] truncate" title={entry.caption || ""}>
                                            {entry.caption || "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            {posts.length > 0 && (
                <div className="pt-4 flex items-center justify-between gap-3 flex-wrap text-sm">
                    <p className="text-gray-500">
                        Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, posts.length)} of {posts.length} post{posts.length !== 1 ? "s" : ""}
                    </p>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-gray-300 hover:border-orange-500/40 hover:text-orange-400 transition disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:text-gray-300"
                        ><ChevronLeft size={14} />Previous</button>
                        {pageList(safePage, totalPages).map((n, i) => n === "…"
                            ? <span key={`e${i}`} className="px-1 text-gray-500">…</span>
                            : (
                                <button
                                    key={n} type="button" onClick={() => setPage(n)}
                                    className={`min-w-[32px] px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${n === safePage ? "bg-orange-500 text-white" : "border border-white/10 text-gray-300 hover:border-orange-500/40 hover:text-orange-400"}`}
                                >{n}</button>
                            ))}
                        <button
                            type="button" onClick={() => setPage(Math.min(totalPages, safePage + 1))} disabled={safePage >= totalPages}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-gray-300 hover:border-orange-500/40 hover:text-orange-400 transition disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:text-gray-300"
                        >Next<ChevronRight size={14} /></button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlannedPosts;